import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueries,
  useQueryClient,
  UseQueryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { graphqlFetcher, restFetcher, restPoster, restPutter } from "~/api/fetchers";
import { useMeQuery } from "~/api/me/use-me-query";
import {
  CreatedUser,
  createdUserSchema,
  CreateUser,
  UpdateUser,
  UserSearchFilter,
  UserSearchResponse,
  userSearchResponseSchema,
} from "~/api/users/types";
import { createFilter } from "~/api/utils";
import { IUser } from "~/types";

import {
  GetUserDocument,
  GetUserQuery,
  GetUserQueryVariables,
  useEditableFeaturesQuery,
  useGetUserQuery,
  useSecurityRolesQuery,
  useUserPrivilegesQuery,
  type UserPrivilegesQuery,
} from "./users.generated";

/**
 * TODO: Suspense version of generated get user graphql query should be
 * either changed to using the user REST endpoint or avoid using suspense
 */
const useGetUserSuspenseQuery = <TData = GetUserQuery, TError = unknown>(
  variables: GetUserQueryVariables,
  options?: Omit<UseQueryOptions<GetUserQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<GetUserQuery, TError, TData>["queryKey"];
  }
) => {
  return useSuspenseQuery<GetUserQuery, TError, TData>({
    queryKey: ["getUser", variables],
    queryFn: graphqlFetcher<GetUserQuery, GetUserQueryVariables>(GetUserDocument, variables),
    ...options,
  });
};

useGetUserQuery.getKey = (variables: GetUserQueryVariables) => ["getUser", variables];

export const useUserDetails = (id: number) => {
  const { data } = useGetUserSuspenseQuery({ id });
  // TODO: Removing the IUser type here would be ideal
  return data?.user as IUser;
};
useUserDetails.getKey = (id: number) => ["getUser", { id }];

export const useUserPrivileges = () => {
  const { data } = useUserPrivilegesQuery(undefined, { staleTime: Infinity });
  return data?.userPrivileges ?? ([] as UserPrivilegesQuery["userPrivileges"]);
};

export const useSecurityRoles = () => {
  const { data, ...rest } = useSecurityRolesQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  return {
    data: data?.userSecurityRoles,
    ...rest,
  };
};

export const useEditableFeatures = () => {
  const { data, ...rest } = useEditableFeaturesQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  return {
    data: data?.editableFeatures.features,
    ...rest,
  };
};

const MINS_15 = 15 * 60 * 1000;

const getUserQueryFunction = (userId: number) => {
  return restFetcher<IUser>(`/v1/users/${userId}`);
};

export const useGetUsersByIdsQuery = (userIds: number[]) => {
  const queries = userIds.map(userId => ({
    queryKey: ["user", userId],
    queryFn: () => getUserQueryFunction(userId),
    staleTime: MINS_15,
  }));

  const results = useQueries({ queries });
  const isLoading = results.some(result => result.isLoading);
  const isError = results.some(result => result.isError);
  const data = results.map(result => result.data).filter(u => u !== null && u !== undefined);

  return { data, isLoading, isError };
};

export function useUpdateNotedIdUserMutation() {
  const queryClient = useQueryClient();
  const { data: { user: me } = {} } = useMeQuery();

  return useMutation({
    ...usersQueries.update,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: useUserDetails.getKey(data.id) });
      if (me?.id === data.id) {
        queryClient.invalidateQueries({ queryKey: useMeQuery.getKey() });
      }
    },
  });
}

export const usersQueries = {
  all: () => ["users"],
  lists: () => ({
    queryKey: [...usersQueries.all(), "list"],
  }),
  list: (filter: UserSearchFilter) => {
    const searchParams = createFilter<UserSearchFilter>(filter);
    return queryOptions({
      queryKey: [...usersQueries.lists().queryKey, filter],
      queryFn: async ({ signal }) => {
        const response = await restFetcher<UserSearchResponse>(`/v1/users?${searchParams}`, {
          signal,
        });
        return userSearchResponseSchema.parse(response);
      },
      placeholderData: keepPreviousData,
    });
  },
  details: () => [...usersQueries.all(), "detail"],
  detail: (id: number) =>
    queryOptions({
      queryKey: [...usersQueries.details(), id],
      queryFn: async () => {
        const response = await restFetcher<CreatedUser>(`/v1/users/${id}`);
        return createdUserSchema.parse(response);
      },
    }),
  add: {
    mutationFn: async (user: CreateUser) => {
      const response = await restPoster<CreateUser, CreatedUser>("/v2/users", user);
      return createdUserSchema.parse(response);
    },
  },
  update: {
    mutationFn: async (user: UpdateUser) => {
      const response = await restPutter<UpdateUser>(`/v2/users/${user.id}`, user);
      return createdUserSchema.parse(response);
    },
  },
};
